import { PureComponent } from 'react'

import moment, { type Moment } from 'moment'
import {
  DateRangePicker as ReactDatesRangePicker,
  type DateRangePickerShape,
  type FocusedInputShape,
} from 'react-dates'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { TransparentIconButton } from '../Button'
import { IconCalendar, IconPointerRight, IconX } from '../Icon'
import { DatepickerCalendarPresets } from './components/DatepickerCalendarPresets'
import { DatepickerNextNavButton } from './components/DatepickerNextNavButton'
import { DatepickerPrevNavButton } from './components/DatepickerPrevNavButton'
import { DeprecatedDatePickerStyled } from './styles/DatepickerStyled'

const PrevNavButton = <DatepickerPrevNavButton />
const NextNavButton = <DatepickerNextNavButton />

/** @deprecated Use the new DatePicker component instead */
export interface IDeprecatedDateRangePickerProps extends DateRangePickerShape {
  /** Set the initial StartDate value, when the Datepicker is controlled */
  initialStartDate?: Moment
  /** Set the initial EndDate value, when the Datepicker is controlled */
  initialEndDate?: Moment
  /**
   * Array of preset dates
   * @example
   * [{ name: string; start: Moment; end: Moment }]
   */
  presets?: { label: string; start: Moment; end: Moment }[]
  error?: boolean
}

interface IDateRangePickerState {
  startDate: Moment
  endDate: Moment
  focusedInput: FocusedInputShape
}

/** @deprecated Use the new DatePicker component instead */
export class DeprecatedDateRangePicker extends PureComponent<
  IDeprecatedDateRangePickerProps,
  IDateRangePickerState
> {
  static displayName = 'DeprecatedDateRangePicker'

  static defaultProps = {
    onFocusChange: () => undefined,
    onDatesChange: () => undefined,
    presets: [
      {
        label: 'Today',
        start: moment(),
        end: moment(),
      },
      {
        label: 'Last 7 Days',
        start: moment().subtract(1, 'week'),
        end: moment(),
      },
      {
        label: 'Last 30 Days',
        start: moment().subtract(30, 'days'),
        end: moment(),
      },
      {
        label: 'This Month',
        start: moment().startOf('month'),
        end: moment().endOf('month'),
      },
      {
        label: 'Last Month',
        start: moment().subtract(1, 'month').startOf('month'),
        end: moment().subtract(1, 'month').endOf('month'),
      },
    ],
  }

  readonly state = {
    endDate: this.props.initialEndDate,
    focusedInput: null,
    startDate: this.props.initialStartDate,
  }

  renderArrow() {
    return <IconPointerRight />
  }

  renderCloseButton() {
    return (
      <div>
        <TransparentIconButton aria-label='Close' iconComponent={<IconX />} />
      </div>
    )
  }

  onDatesChange = ({ startDate, endDate }) => {
    this.setState(
      {
        endDate,
        focusedInput:
          this.state.focusedInput && startDate === null && endDate === null
            ? 'startDate'
            : this.state.focusedInput,
        startDate,
      },
      () => this.props.onDatesChange({ startDate, endDate })
    )
  }

  onFocusChange = (focusedInput) => {
    this.setState(
      {
        focusedInput:
          this.state.focusedInput === null && !this.state.startDate
            ? 'startDate'
            : focusedInput,
      },
      () => this.props.onFocusChange(this.state.focusedInput)
    )
  }

  onPresetClick = (preset) => {
    this.setState(
      {
        endDate: preset.end,
        focusedInput: null,
        startDate: preset.start,
      },
      () =>
        this.props.onDatesChange({
          startDate: preset.start,
          endDate: preset.end,
        })
    )
  }

  render() {
    const {
      presets,
      error,
      startDatePlaceholderText,
      endDatePlaceholderText,
      ...otherProps
    } = this.props
    const { focusedInput, startDate, endDate } = this.state

    return (
      <DeprecatedDatePickerStyled error={error}>
        <ReactDatesRangePicker
          navPrev={PrevNavButton}
          navNext={NextNavButton}
          isOutsideRange={() => false}
          daySize={34}
          verticalHeight={500}
          calendarInfoPosition='after'
          renderCalendarInfo={() => (
            <DatepickerCalendarPresets
              presets={presets}
              onClick={(preset) => this.onPresetClick(preset)}
            />
          )}
          customArrowIcon={<this.renderArrow />}
          customCloseIcon={<this.renderCloseButton />}
          focusedInput={focusedInput}
          startDate={startDate}
          endDate={endDate}
          onDatesChange={this.onDatesChange}
          onFocusChange={this.onFocusChange}
          renderDayContents={(day) => <div>{day.format('D')}</div>}
          showDefaultInputIcon={true}
          inputIconPosition='after'
          customInputIcon={
            <IconCalendar color='textPrimary' ariaLabel='Open calendar' />
          }
          verticalSpacing={0}
          startDatePlaceholderText={startDatePlaceholderText || 'Start date'}
          endDatePlaceholderText={endDatePlaceholderText || 'End date'}
          {...otherProps}
        />
      </DeprecatedDatePickerStyled>
    )
  }
}
